/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - ScoreBadge Component
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Badge para mostrar puntuación de confianza de coordenadas.
 * 4 niveles con código de color (DS v2 D2: umbrales 85/70/50):
 *   - high (≥85): lime
 *   - medium (70-84): blue
 *   - low (50-69): amber
 *   - critical (<50): red
 *
 * Uso:
 *   <ScoreBadge score={92} />
 *   <ScoreBadge score={45} originalScore={20} />
 *
 * ══════════════════════════════════════════════════════════════════════════════
 */

import { colors, typography, borderRadius, spacing, getScoreClass } from '../design-tokens';
// scoreColors derivado de tokens DS — NO hardcodear valores rgba aquí

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface ScoreBadgeProps {
  /** Puntuación actual (0-100) */
  score: number;
  /** Puntuación original antes de normalización (opcional) */
  originalScore?: number;
  /** Mostrar flecha de transición cuando hay originalScore */
  showTransition?: boolean;
  /** Tamaño del badge */
  size?: 'sm' | 'md';
  /** Clases CSS adicionales */
  className?: string;
  /** ID para testing */
  testId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE TAMAÑOS
// ─────────────────────────────────────────────────────────────────────────────

const sizeConfig = {
  sm: {
    padding: '3px 6px',
    fontSize: typography.fontSize['2xs'], // 10px
    minWidth: '32px',
  },
  md: {
    padding: `${spacing[1]} ${spacing[3]}`, // 4px 8px
    fontSize: typography.fontSize.xs, // 11px
    minWidth: '36px',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────

export function ScoreBadge({
  score,
  originalScore,
  showTransition = true,
  size = 'md',
  className = '',
  testId,
}: ScoreBadgeProps) {
  const scoreClass = getScoreClass(score);
  const sizeStyles = sizeConfig[size];

  const hasTransition = originalScore !== undefined && showTransition;

  return (
    <div
      data-testid={testId ?? 'score-badge'}
      data-score-class={scoreClass}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing[1],
        fontFamily: typography.fontFamily.mono,
        fontSize: sizeStyles.fontSize,
      }}
    >
      {/* Score original (si existe) */}
      {hasTransition && (
        <>
          <span
            data-testid="score-original"
            style={{ color: colors.fg.muted }}
          >
            {originalScore}
          </span>
          <span
            aria-hidden="true"
            style={{
              color: colors.fg.subtle,
              fontSize: typography.fontSize['2xs'],
            }}
          >
            →
          </span>
        </>
      )}

      {/* Score actual con badge coloreado */}
      <span
        data-testid="score-value"
        style={{
          display: 'inline-block',
          minWidth: sizeStyles.minWidth,
          padding: sizeStyles.padding,
          textAlign: 'center',
          fontWeight: typography.fontWeight.semibold,
          borderRadius: borderRadius.sm,
          backgroundColor: colors.scoresBg[scoreClass],
          color: colors.scores[scoreClass],
        }}
      >
        {score}
      </span>
    </div>
  );
}

export default ScoreBadge;
